import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Field, SettingsCard } from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { DatePicker } from '@allrouted/ui/DatePicker';
import { Drawer } from '@allrouted/ui/Drawer';
import { IconButton, PlusIcon, TrashIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';
import { ROUTE_COLORS } from '../../lib/route-color';

/**
 * Driver assignment: who drives which vehicle.
 *
 * Normal day is who drives each vehicle on an ordinary day. It is the same
 * value as the Driver field on each vehicle -- edit either and both change --
 * and every territory has one from the start.
 *
 * A date exception replaces Normal day on one date: holiday cover, a sick day.
 * Only the vehicles changed in it differ; the rest keep their Normal day
 * driver, including if Normal day changes later.
 */

interface AssignmentRow extends Record<string, unknown> {
  id: string;
  name: string;
  is_default: boolean;
  date: string | null;
  changed_count: number;
}

interface Entry {
  vehicle_id: string;
  technician_id: string | null;
  normal_technician_id: string | null;
  vehicle_name: string;
  vehicle_idx: number | null;
  vehicle_color: string | null;
}

interface Detail extends Record<string, unknown> {
  id: string;
  name: string;
  is_default: boolean;
  date: string | null;
  entries: Entry[];
}

/** The same rule the board uses, so a van is one colour across the product. */
function colorFor(entry: Entry): string {
  if (entry.vehicle_color) return entry.vehicle_color;
  const n = entry.vehicle_idx ?? 1;
  return ROUTE_COLORS[(n - 1) % ROUTE_COLORS.length] as string;
}

function longDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function AssignmentPage({ territoryId }: { territoryId: string }) {
  const ui = useUi();
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [pairing, setPairing] = useState<Record<string, string | null>>({});
  const [drivers, setDrivers] = useState<{ id: string; name: string }[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const loadList = useCallback(async () => {
    const [{ assignments }, { technicians }] = await Promise.all([
      api.driverAssignments(territoryId),
      api.technicians(territoryId),
    ]);
    setRows(assignments as AssignmentRow[]);
    setDrivers(technicians as { id: string; name: string }[]);
    setOpenId((current) =>
      current && (assignments as AssignmentRow[]).some((a) => a.id === current)
        ? current
        : ((assignments[0] as AssignmentRow | undefined)?.id ?? null),
    );
  }, [territoryId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadDetail = useCallback(async () => {
    if (!openId) return;
    const value = (await api.driverAssignment(territoryId, openId)) as unknown as Detail;
    setDetail(value);
    const next: Record<string, string | null> = {};
    for (const e of value.entries) next[e.vehicle_id] = e.technician_id;
    setPairing(next);
    setEditing(false);
  }, [territoryId, openId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  /*
   * A driver in two vans at once is nearly always a slip mid-edit, so it is
   * shown rather than blocked -- there are real days where one person moves
   * between two vehicles.
   */
  const doubled = new Set(
    Object.values(pairing)
      .filter((id): id is string => id !== null)
      .filter((id, i, all) => all.indexOf(id) !== i),
  );

  const driverName = (id: string | null) => (id ? (drivers.find((d) => d.id === id)?.name ?? 'Unknown driver') : 'No driver');

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.saveDriverAssignment(territoryId, detail.id, {
        entries: detail.entries.map((e) => ({
          vehicleId: e.vehicle_id,
          technicianId: pairing[e.vehicle_id] ?? null,
        })),
      });
      await loadDetail();
      await loadList();
      ui.notify(
        'ok',
        detail.is_default ? 'Normal day saved' : 'Exception saved',
        detail.is_default ? 'Each vehicle’s Driver field now shows the same.' : `Applies on ${longDate(detail.date)}.`,
      );
    } catch (err) {
      ui.notify('error', 'Could not save', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const create = async (name: string, date: string) => {
    try {
      /* Starts as a copy of Normal day: change only the vehicles that differ that day. */
      const created = (await api.createDriverAssignment(territoryId, { name, date })) as unknown as Detail;
      setNewOpen(false);
      await loadList();
      setOpenId(created.id);
      ui.notify('ok', 'Exception added', 'It starts as a copy of Normal day. Change the vehicles that differ.');
    } catch (err) {
      ui.notify('error', 'Could not add it', err instanceof ApiError ? err.message : String(err));
    }
  };

  const remove = async () => {
    if (!detail || detail.is_default) return;
    const ok = await ui.confirm({
      title: `Delete “${detail.name}”?`,
      body: `${longDate(detail.date)} goes back to Normal day.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteDriverAssignment(territoryId, detail.id);
      setOpenId(null);
      setDetail(null);
      await loadList();
      ui.notify('ok', 'Exception deleted');
    } catch (err) {
      ui.notify('error', 'Could not delete', err instanceof ApiError ? err.message : String(err));
    }
  };

  const normal = rows.find((r) => r.is_default);
  const exceptions = rows.filter((r) => !r.is_default);

  return (
    <div className="split">
      <div className="side">
        <div className="side-head">
          <h2>Driver Assignment</h2>
        </div>
        <div className="side-list">
          {normal ? (
            <button
              type="button"
              className="notif-row"
              data-selected={normal.id === openId ? 'true' : undefined}
              onClick={() => setOpenId(normal.id)}
            >
              <b>{normal.name}</b>
              <small>Every day, unless an exception below says otherwise</small>
            </button>
          ) : null}

          <div className="assign-section">
            <span>Date exceptions</span>
            <IconButton label="Add an exception for a date" onClick={() => setNewOpen(true)}>
              <PlusIcon />
            </IconButton>
          </div>
          {exceptions.length === 0 ? (
            <p className="assign-empty">
              None. Add one when a day is covered differently: a holiday, a sick day, a swapped van.
            </p>
          ) : (
            exceptions.map((r) => (
              <button
                key={r.id}
                type="button"
                className="notif-row"
                data-selected={r.id === openId ? 'true' : undefined}
                onClick={() => setOpenId(r.id)}
              >
                <b>{longDate(r.date)}</b>
                <small>
                  {r.name}
                  {' · '}
                  {r.changed_count === 0 ? 'no changes yet' : `${r.changed_count} vehicle${r.changed_count === 1 ? '' : 's'} changed`}
                </small>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="detail">
        {detail ? (
          <SettingsCard
            title={detail.is_default ? detail.name : `${detail.name} · ${longDate(detail.date)}`}
            description={
              detail.is_default
                ? 'Who drives each vehicle on an ordinary day. This is the same as the Driver field on each vehicle: change it here or there and both update.'
                : `Replaces Normal day on ${longDate(detail.date)} only. Vehicles you leave as they normally are keep their Normal day driver.`
            }
            editing={editing}
            saving={saving}
            onEdit={() => setEditing(true)}
            onCancel={() => void loadDetail()}
            onSave={() => void save()}
            actions={
              detail.is_default ? undefined : (
                <IconButton tone="danger" label="Delete this exception" onClick={() => void remove()}>
                  <TrashIcon />
                </IconButton>
              )
            }
          >
            {doubled.size > 0 ? (
              <div className="banner" data-tone="warn">
                <div>
                  <b>Someone is in two vehicles</b>
                  <p>
                    That is allowed (a technician can move between vans in a day), but it is worth a
                    second look before saving.
                  </p>
                </div>
              </div>
            ) : null}

            {detail.entries.length === 0 ? (
              <div className="empty">No vehicles in this territory yet. Add them under Vehicles.</div>
            ) : drivers.length === 0 ? (
              <div className="empty">No drivers yet. Add them under Drivers, then assign them here.</div>
            ) : (
              detail.entries.map((e) => {
                const current = pairing[e.vehicle_id] ?? null;
                const changed = !detail.is_default && current !== e.normal_technician_id;
                return (
                  <Field
                    key={e.vehicle_id}
                    label={
                      (
                        <span className="assign-label">
                          <span className="assign-dot" style={{ background: colorFor(e) }} />
                          {e.vehicle_name}
                        </span>
                      ) as unknown as string
                    }
                  >
                    <span className="assign-control">
                      <Select
                        value={current ?? ''}
                        options={[
                          { value: '', label: 'No driver' },
                          ...drivers.map((d) => ({ value: d.id, label: d.name })),
                        ]}
                        onChange={(v) => setPairing((p) => ({ ...p, [e.vehicle_id]: v === '' ? null : v }))}
                      />
                      {changed ? (
                        <span className="assign-changed" title="Different from Normal day">
                          Changed · normally {driverName(e.normal_technician_id)}
                        </span>
                      ) : !detail.is_default ? (
                        <span className="assign-same">As Normal day</span>
                      ) : null}
                    </span>
                  </Field>
                );
              })
            )}
          </SettingsCard>
        ) : (
          <div className="empty">Loading…</div>
        )}
      </div>

      <NewExceptionDrawer
        open={newOpen}
        onClose={() => setNewOpen(false)}
        taken={exceptions.map((r) => r.date ?? '')}
        onCreate={(name, date) => void create(name, date)}
      />
    </div>
  );
}

function NewExceptionDrawer({
  open,
  onClose,
  taken,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  taken: string[];
  onCreate: (name: string, date: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState('');
  const [date, setDate] = useState(today);
  const clash = taken.includes(date);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Exception for a date"
      subtitle="A different driver assignment for one day"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!date || clash}
            onClick={() => onCreate(name.trim() || 'Exception', date)}
          >
            Add exception
          </button>
        </>
      }
    >
      <p className="disclosure-note">
        For the chosen date only, this replaces Normal day. It starts as a copy of Normal day, so you
        change just the vehicles that are covered differently. Every other date keeps Normal day.
      </p>
      <Field label="Date">
        <DatePicker value={date} today={today} onChange={setDate} />
      </Field>
      {clash ? <p className="disclosure-note">That date already has an exception. Open it from the list instead.</p> : null}
      <Field label="Reason" hint="Optional">
        <input
          className="text-input"
          value={name}
          placeholder="e.g. Thanksgiving cover, Sam off sick"
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
    </Drawer>
  );
}
